export type Mood = 'anxious' | 'exhausted' | 'empty' | 'angry' | 'sad' | 'lost' | 'lonely' | 'fearful';
export type TransformGoal = 'calm' | 'loved' | 'fulfilled' | 'power' | 'joy' | 'release' | 'safe' | 'free';

export interface TreeMatch {
  mood: Mood;
  goal: TransformGoal;
  moodLabel: string;
  moodEmoji: string;
  goalLabel: string;
  goalEmoji: string;
  treeName: string;
  treeEn: string;
  trait: string;
  reason: string;
  affirmation: string;
  color: string;
  image: string;
}

export const moods: { key: Mood; emoji: string; label: string }[] = [
  { key: 'anxious', emoji: '🍂', label: '焦慮' },
  { key: 'exhausted', emoji: '🕯️', label: '疲憊' },
  { key: 'empty', emoji: '🪶', label: '空虛' },
  { key: 'angry', emoji: '🌩️', label: '憤怒' },
  { key: 'sad', emoji: '🌧️', label: '悲傷' },
  { key: 'lost', emoji: '🌫️', label: '迷惘' },
  { key: 'lonely', emoji: '🏔️', label: '孤獨' },
  { key: 'fearful', emoji: '🌑', label: '恐懼' },
];

export const transformGoals: { key: TransformGoal; emoji: string; label: string }[] = [
  { key: 'calm', emoji: '🍃', label: '平靜' },
  { key: 'loved', emoji: '🕊️', label: '被愛' },
  { key: 'fulfilled', emoji: '🌾', label: '充實' },
  { key: 'power', emoji: '🪨', label: '有力量' },
  { key: 'joy', emoji: '🌸', label: '喜悅' },
  { key: 'release', emoji: '🌊', label: '釋懷' },
  { key: 'safe', emoji: '🪹', label: '安全感' },
  { key: 'free', emoji: '🪁', label: '自由' },
];

export const treeMatches: TreeMatch[] = [
  {
    mood: 'anxious',
    goal: 'calm',
    moodLabel: '焦慮',
    moodEmoji: '🍂',
    goalLabel: '平靜',
    goalEmoji: '🍃',
    treeName: '橡樹',
    treeEn: 'Oak',
    trait: '穩定 · 紮根',
    reason: '橡樹嘅根系深入泥土幾十米，佢會將你飄浮嘅思緒重新帶落地面。攬住橡樹粗糙厚實嘅樹皮，感受佢幾百年不變嘅穩定頻率，你嘅焦慮會慢慢沉澱落嚟——你唔需要控制一切，土地會接住你。',
    affirmation: '我嘅存在，本身就已經足夠穩固。唔需要控制一切，都可以平安。',
    color: 'primary',
    image: 'https://readdy.ai/api/search-image?query=A%20massive%20ancient%20English%20oak%20tree%20with%20thick%20deeply%20textured%20bark%20and%20sprawling%20branches%20standing%20alone%20in%20a%20sunlit%20green%20meadow%20field%2C%20clear%20blue%20sky%20background%20with%20soft%20white%20clouds%2C%20realistic%20nature%20documentary%20photography%20style%20with%20sharp%20detail%20on%20bark%20texture%20and%20leaves%2C%20vibrant%20natural%20green%20tones%2C%20professional%20landscape%20photography%2C%20isolated%20majestic%20specimen%20tree%20in%20full%20frame%20composition&width=800&height=1000&seq=tree-oak-real-v5-01&orientation=portrait',
  },
  {
    mood: 'exhausted',
    goal: 'power',
    moodLabel: '疲憊',
    moodEmoji: '🕯️',
    goalLabel: '有力量',
    goalEmoji: '🪨',
    treeName: '鳳凰木',
    treeEn: 'Flame Tree',
    trait: '熱情 · 綻放',
    reason: '鳳凰木開花嘅時候似火焰咁燃燒，佢嘅生命力係唔需要理由嘅。攬住佢粗壯嘅樹幹，你會記起自己都曾經係一個可以純粹因為開心而跳起嘅人。疲倦嘅時候，佢會將沉睡嘅能量喚醒，提醒你：你內在嘅火光從未熄滅。',
    affirmation: '我值得盛開，唔需要為咗任何人縮細自己。我嘅能量係與生俱來嘅。',
    color: 'accent',
    image: 'https://readdy.ai/api/search-image?query=A%20spectacular%20royal%20poinciana%20flame%20tree%20in%20full%20bloom%20with%20dense%20clusters%20of%20vivid%20fiery%20orange%20red%20flowers%20covering%20the%20entire%20wide%20umbrella%20shaped%20canopy%2C%20bright%20tropical%20blue%20sky%20background%2C%20photorealistic%20nature%20photography%20style%20with%20sharp%20detail%20on%20the%20flame%20colored%20blossoms%20and%20fern%20like%20green%20leaves%2C%20intense%20saturated%20warm%20colors%2C%20full%20frame%20composition%20capturing%20the%20breathtaking%20flowering%20display%20against%20the%20sky&width=800&height=1000&seq=tree-flame-real-v5-02&orientation=portrait',
  },
  {
    mood: 'empty',
    goal: 'fulfilled',
    moodLabel: '空虛',
    moodEmoji: '🪶',
    goalLabel: '充實',
    goalEmoji: '🌾',
    treeName: '銀杏樹',
    treeEn: 'Ginkgo',
    trait: '古老 · 智慧',
    reason: '銀杏喺地球活咗兩億年，比恐龍仲耐。抱住佢就好似打開時間嘅另一個維度——佢嘅扇形葉片由同一點擴散到無限方向，提醒你：你嘅生命都有無限可能。空虛感只係一個轉折點，唔係終點。',
    affirmation: '我信任時間，時間亦都信任我。每一個空白都係一個新嘅起點。',
    color: 'secondary',
    image: 'https://readdy.ai/api/search-image?query=A%20tall%20ancient%20ginkgo%20tree%20with%20distinctive%20fan-shaped%20golden%20yellow%20autumn%20leaves%20glowing%20brilliantly%20in%20warm%20afternoon%20sunlight%2C%20carpet%20of%20fallen%20yellow%20leaves%20at%20the%20base%2C%20clear%20blue%20sky%20backdrop%2C%20photorealistic%20nature%20documentary%20photography%20style%20with%20sharp%20detail%20on%20unique%20leaf%20shapes%20and%20deeply%20furrowed%20bark%2C%20rich%20golden%20amber%20tones%2C%20majestic%20solitary%20tree%20in%20full%20autumnal%20color&width=800&height=1000&seq=tree-ginkgo-real-v5-04&orientation=portrait',
  },
  {
    mood: 'angry',
    goal: 'release',
    moodLabel: '憤怒',
    moodEmoji: '🌩️',
    goalLabel: '釋懷',
    goalEmoji: '🌊',
    treeName: '柳樹',
    treeEn: 'Willow',
    trait: '柔韌 · 釋放',
    reason: '柳樹嘅枝條睇落柔弱，但暴風雨都吹唔跌佢——佢嘅秘訣係「順應」。抱住柳樹，你會學到：憤怒可以流動，唔需要硬碰硬。讓情緒穿過身體，就好似風穿過柳枝，然後輕輕咁放低。',
    affirmation: '我嘅柔軟係我最大嘅力量。讓情緒流動，然後放手，我仍然完整。',
    color: 'secondary',
    image: 'https://readdy.ai/api/search-image?query=A%20large%20graceful%20weeping%20willow%20tree%20with%20long%20cascading%20drooping%20branches%20touching%20the%20ground%2C%20standing%20beside%20a%20calm%20serene%20pond%20with%20gentle%20reflections%2C%20soft%20diffused%20morning%20sunlight%20filtering%20through%20the%20hanging%20foliage%2C%20peaceful%20natural%20landscape%2C%20realistic%20nature%20photography%20style%20with%20crisp%20detail%20on%20the%20slender%20hanging%20branches%20and%20leaves%2C%20tranquil%20pastoral%20scene%20with%20clean%20composition&width=800&height=1000&seq=tree-willow-real-v5-06&orientation=portrait',
  },
  {
    mood: 'sad',
    goal: 'loved',
    moodLabel: '悲傷',
    moodEmoji: '🌧️',
    goalLabel: '被愛',
    goalEmoji: '🕊️',
    treeName: '櫻花樹',
    treeEn: 'Cherry Blossom',
    trait: '溫柔 · 接納',
    reason: '櫻花教我哋，短暫嘅美麗都值得被記住。抱住櫻花樹嘅時候，佢會提醒你：悲傷同花瓣一樣，都會過去，但每一場飄落都真實。你唔需要永遠燦爛，有人會因為你真實嘅模樣而愛你。',
    affirmation: '我可以又脆弱、又美麗，同時存在。溫柔本身就係一種強大。',
    color: 'secondary',
    image: 'https://readdy.ai/api/search-image?query=A%20full%20blooming%20cherry%20blossom%20tree%20covered%20entirely%20in%20soft%20pink%20flowers%20with%20delicate%20branches%20spreading%20outward%2C%20petals%20falling%20gently%20in%20warm%20spring%20breeze%2C%20clear%20pale%20blue%20sky%20background%2C%20photorealistic%20nature%20photography%20style%20with%20high%20detail%20on%20individual%20blossoms%20and%20textured%20bark%2C%20soft%20natural%20morning%20light%2C%20clean%20centered%20composition%20capturing%20the%20entire%20tree%20in%20bloom%20against%20the%20sky&width=800&height=1000&seq=tree-sakura-real-v5-03&orientation=portrait',
  },
  {
    mood: 'lost',
    goal: 'joy',
    moodLabel: '迷惘',
    moodEmoji: '🌫️',
    goalLabel: '喜悅',
    goalEmoji: '🌸',
    treeName: '松樹',
    treeEn: 'Pine',
    trait: '長青 · 希望',
    reason: '松樹一年四季都係綠色，佢唔會因為季節改變自己，亦唔會忘記每一個日出嘅溫暖。迷惘嘅時候攬住佢，佢會幫你記起：方向有時唔係用眼搵，而係用心感受。你內在嘅指南針從未失靈。',
    affirmation: '我嘅快樂唔會用完，每一次呼吸都係新嘅開始。方向會喺行嘅過程中慢慢浮現。',
    color: 'primary',
    image: 'https://readdy.ai/api/search-image?query=A%20tall%20majestic%20pine%20tree%20standing%20solitary%20on%20a%20rocky%20mountain%20slope%2C%20deep%20green%20needles%20with%20textured%20rough%20brown%20bark%2C%20misty%20morning%20fog%20wrapping%20around%20the%20branches%20with%20soft%20sunlight%20breaking%20through%2C%20distant%20mountain%20peaks%20in%20background%2C%20photorealistic%20nature%20photography%20style%20with%20crisp%20detail%20on%20pine%20needles%20and%20bark%20texture%2C%20serene%20contemplative%20mood%2C%20full%20composition%20of%20the%20evergreen%20tree%20against%20dramatic%20natural%20landscape&width=800&height=1000&seq=tree-pine-real-v5-05&orientation=portrait',
  },
  {
    mood: 'lonely',
    goal: 'safe',
    moodLabel: '孤獨',
    moodEmoji: '🏔️',
    goalLabel: '安全感',
    goalEmoji: '🪹',
    treeName: '榕樹',
    treeEn: 'Banyan',
    trait: '庇護 · 連結',
    reason: '榕樹用氣根編織成一個巨大嘅庇護所——佢唔係一棵樹，係一個社群。攬住佢嘅時候，你會感受到一種無形嘅保護：你唔係一個人。佢用百年生命提你：真正嘅連結，唔需要言語，只需要存在。',
    affirmation: '我被萬物溫柔地承托住。孤獨只係一個感覺，唔係我嘅身份。我永遠同大地連結。',
    color: 'primary',
    image: 'https://readdy.ai/api/search-image?query=A%20magnificent%20ancient%20banyan%20tree%20with%20hundreds%20of%20thick%20aerial%20roots%20dropping%20from%20its%20massive%20spreading%20canopy%20forming%20a%20natural%20cathedral%20like%20structure%2C%20dappled%20sunlight%20filtering%20through%20the%20dense%20green%20leaves%2C%20warm%20tropical%20atmosphere%2C%20photorealistic%20nature%20photography%20with%20sharp%20detail%20on%20the%20complex%20root%20structures%20and%20textured%20trunk%2C%20awe%20inspiring%20scale%20of%20the%20enormous%20tree%20against%20human%20proportions%20for%20scale&width=800&height=1000&seq=tree-banyan-real-v5-07&orientation=portrait',
  },
  {
    mood: 'fearful',
    goal: 'free',
    moodLabel: '恐懼',
    moodEmoji: '🌑',
    goalLabel: '自由',
    goalEmoji: '🪁',
    treeName: '樟樹',
    treeEn: 'Camphor',
    trait: '淨化 · 清晰',
    reason: '樟樹散發住一種天然嘅清香——古人話佢可以辟邪，其實係佢嘅氣味可以鎮靜神經系統。攬住樟樹粗糙而溫暖嘅樹皮，吸入佢獨有嘅香氣，你嘅恐懼會慢慢散開。恐懼係迷霧，而你內在嘅光永遠比佢強大。',
    affirmation: '我內在嘅光，比一切恐懼都更加明亮。每一次呼吸，都係一次釋放。我可以睇得更遠。',
    color: 'accent',
    image: 'https://readdy.ai/api/search-image?query=A%20tall%20stately%20camphor%20tree%20with%20dense%20evergreen%20foliage%20and%20deeply%20textured%20warm%20brown%20bark%2C%20bathed%20in%20soft%20golden%20morning%20sunlight%2C%20standing%20in%20a%20peaceful%20Japanese%20temple%20garden%20with%20subtle%20moss%20covered%20ground%2C%20serene%20meditative%20atmosphere%2C%20realistic%20nature%20photography%20with%20crisp%20detail%20on%20the%20aromatic%20leaves%20and%20rich%20bark%20texture%2C%20clean%20tranquil%20composition%20capturing%20the%20majestic%20solitary%20tree&width=800&height=1000&seq=tree-camphor-real-v5-08&orientation=portrait',
  },
];

// Extended matches for all mood+goal combinations
const extendedTreeMatches: Record<string, TreeMatch> = {};

function getKey(mood: Mood, goal: TransformGoal): string {
  return `${mood}::${goal}`;
}

function initExtended() {
  if (Object.keys(extendedTreeMatches).length > 0) return;

  for (const match of treeMatches) {
    extendedTreeMatches[getKey(match.mood, match.goal)] = match;
  }

  for (const mood of moods) {
    for (const goal of transformGoals) {
      const key = getKey(mood.key, goal.key);
      if (!extendedTreeMatches[key]) {
        const moodMatch = treeMatches.find((t) => t.mood === mood.key);
        if (moodMatch) {
          extendedTreeMatches[key] = { ...moodMatch, goal: goal.key, goalLabel: goal.label, goalEmoji: goal.emoji };
        }
      }
    }
  }
}

export function matchTree(mood: Mood, goal: TransformGoal): TreeMatch {
  initExtended();
  const key = getKey(mood, goal);
  if (extendedTreeMatches[key]) return extendedTreeMatches[key];

  const moodFallback = treeMatches.find((t) => t.mood === mood);
  if (moodFallback) return moodFallback;

  return treeMatches[0];
}